import { Alert, Button, Stack } from "@mui/material";
import { SignaturePreparationBanner } from "components/Submission/SignaturePreparationBanner";
import { SubmissionFormProvider } from "contexts/SubmissionFormContext";
import { SubmissionStepType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { stepperStepPath } from "hooks/useStepperNavigation";
import { Navigate, useParams } from "react-router-dom";
import { WASTE_SUBMISSION_STEP_PRESENTATION } from "util/wasteSubmissionStepMetadata";

import { DetailsCard } from "../DetailsCard";
import { StepHeader } from "../StepHeader";
import { DocumentStepStatusBanner } from "./DocumentStepStatusBanner";
import { ReviewerConfigSection } from "./ReviewerPreparation/ReviewerConfigSection";
import { ReviewerPreparationActions } from "./ReviewerPreparation/ReviewerPreparationActions";
import { ReviewerPreparationDialogs } from "./ReviewerPreparation/ReviewerPreparationDialogs";
import { SignatureConfigSection } from "./ReviewerPreparation/SignatureConfigSection";
import { useReviewerPreparationState } from "./ReviewerPreparation/useReviewerPreparationState";

/**
 * Reviewer Preparation step (document forms): staff assign reviewers and the
 * signer, choose the signature type, and send the document for review.
 * Split out of Review & Sign so staff configuration and reviewer/signer
 * action have their own steps — see that file for the review/sign UI.
 *
 * Document-form editing is staff-only; a reviewer/signer deep-linking here has
 * nothing to do, so they're sent to the step that's actually theirs. The
 * redirect is a wrapper around the real content so it can return early
 * without calling the content's hooks first.
 */
export function ReviewerPreparationStep(props: { submissionId: string; facilityId: string }) {
  const { isStaff } = useCurrentUser();
  const { tenantId = "" } = useParams();
  if (!isStaff) {
    return (
      <Navigate
        to={stepperStepPath(
          tenantId,
          props.submissionId,
          WASTE_SUBMISSION_STEP_PRESENTATION[SubmissionStepType.ReviewAndSign].routeSlug
        )}
        replace
      />
    );
  }
  return <ReviewerPreparationStepContent {...props} />;
}

function ReviewerPreparationStatusBanners({
  state: s,
}: {
  state: ReturnType<typeof useReviewerPreparationState>;
}) {
  return (
    <>
      <DocumentStepStatusBanner step={SubmissionStepType.ReviewerPreparation} />

      {s.unattributedIssues.length > 0 && (
        <Alert severity="error">
          {s.unattributedIssues.map((issue, index) => (
            <div key={index}>{issue.message}</div>
          ))}
        </Alert>
      )}

      {s.submission && (
        <SignaturePreparationBanner
          submission={{
            status: s.submission.status,
            signatureType: s.submission.signatureType,
            documents: s.documents.map((d) => ({
              type: d.type,
              externalDocumentId: d.externalDocumentId,
            })),
            reviewers: s.persistedReviewers.map((r) => ({
              isSigner: r.isSigner,
              signatureStatus: r.signatureStatus,
            })),
          }}
        />
      )}

      {s.isRejected && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => s.setRejectionRoutingOpen(true)}>
              Resolve
            </Button>
          }
        >
          This submission has been <strong>rejected</strong> by a reviewer. Use rejection routing to
          determine next steps.
        </Alert>
      )}
    </>
  );
}

function ReviewerPreparationStepBody({
  state: s,
  submissionId,
}: {
  state: ReturnType<typeof useReviewerPreparationState>;
  submissionId: string;
}) {
  return (
    <Stack spacing={4}>
      <ReviewerConfigSection
        disabled={s.disabled}
        reviewRequired={s.reviewRequired}
        onReviewRequiredChange={s.setReviewRequired}
        onAddReviewer={() => s.setReviewerDialogOpen(true)}
        peerReviewRequired={s.peerReviewRequired}
        hasStaffReviewer={s.hasStaffReviewer}
        onPeerReviewToggle={s.handlePeerReviewToggle}
        reviewers={s.reviewers}
        onReviewersChange={s.handleReviewersDatagridChange}
        reviewMode={s.reviewMode}
        setReviewMode={s.setReviewMode}
        submissionDueDate={s.submissionDueDate}
        isStaff={s.isStaff}
        submissionId={submissionId}
        submissionEncampStatus={s.submission?.encampStatus ?? null}
        reviewRequiredIssues={s.reviewRequiredIssues}
      />

      <SignatureConfigSection
        disabled={s.disabled}
        signatureType={s.signatureType}
        onSignatureTypeChange={s.handleSignatureTypeChange}
        onAddSigner={() => s.setSignerDialogOpen(true)}
        signer={s.signer}
        onRemoveSigner={() => {
          if (s.disabled) return;
          s.setSigner(null);
        }}
        submissionDueDate={s.submissionDueDate}
        signatureTypeIssues={s.signatureTypeIssues}
      />

      {!s.disabled && (
        <ReviewerPreparationActions
          saving={s.saving}
          sending={s.sending}
          hasUnsavedChanges={s.hasUnsavedChanges}
          onSaveConfig={s.handleSaveConfig}
          showPrepareForSigning={
            s.canPrepareForSigning && s.isDigitalSignature && s.needsDocumentPreparation
          }
          signer={s.signer}
          displayIssues={s.displayIssues}
          documents={s.documents.map((d) => ({
            id: d.document.id,
            name: d.document.title,
            documentType: d.document.documentType,
            submissionDocumentType: d.type,
            url: d.document.storageLink ?? undefined,
            fileExtension: d.document.fileExtension,
          }))}
          onPrepareForSigning={s.handlePrepareForSigning}
          showSendForReview={
            s.canSendForReview && !(s.isDigitalSignature && s.needsDocumentPreparation)
          }
          sendDisabled={s.sendDisabled}
          onSend={s.handleSend}
          isStaff={s.isStaff}
          advancesWithoutReview={s.advancesWithoutReview}
        />
      )}
    </Stack>
  );
}

function ReviewerPreparationStepDialogsWrapper({
  state: s,
  submissionId,
  facilityId,
}: {
  state: ReturnType<typeof useReviewerPreparationState>;
  submissionId: string;
  facilityId: string;
}) {
  return (
    <ReviewerPreparationDialogs
      submissionId={submissionId}
      facilityId={facilityId}
      saving={s.saving}
      sending={s.sending}
      hasUnsavedChanges={s.hasUnsavedChanges}
      reviewerDialogOpen={s.reviewerDialogOpen}
      onCloseReviewerDialog={() => s.setReviewerDialogOpen(false)}
      reviewers={s.reviewers}
      reviewMode={s.reviewMode}
      peerReviewRequired={s.peerReviewRequired}
      onSaveReviewers={s.handleSaveReviewers}
      signerDialogOpen={s.signerDialogOpen}
      onCloseSignerDialog={() => s.setSignerDialogOpen(false)}
      signer={s.signer}
      signatureType={s.signatureType}
      onChangeSigner={s.setSigner}
      onSaveSigner={s.handleSignerSave}
      reviewersToConfirm={s.reviewersToConfirm}
      onCloseReviewersToConfirm={() => s.setReviewersToConfirm(null)}
      onConfirmReviewersToConfirm={s.handleConfirmReviewersToConfirm}
      signerToConfirm={s.signerToConfirm}
      onCloseSignerToConfirm={() => s.setSignerToConfirm(null)}
      onConfirmSignerToConfirm={s.handleConfirmSignerToConfirm}
      preparationDialogOpen={s.preparationDialogOpen}
      onClosePreparationDialog={() => s.setPreparationDialogOpen(false)}
      rejectionRoutingOpen={s.rejectionRoutingOpen}
      onCloseRejectionRouting={() => s.setRejectionRoutingOpen(false)}
      advanceConfirmOpen={s.advanceConfirmOpen}
      onCloseAdvanceConfirm={() => s.setAdvanceConfirmOpen(false)}
      onConfirmAdvance={s.handleConfirmAdvance}
      finalApprovalReviewers={s.finalApprovalReviewers}
      onCloseFinalApproval={() => s.setFinalApprovalReviewers(null)}
      onConfirmFinalApproval={s.handleConfirmFinalApproval}
    />
  );
}

function ReviewerPreparationStepContent({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const s = useReviewerPreparationState(submissionId);

  return (
    <SubmissionFormProvider issues={s.displayIssues} dueDateWarningsByUserId={s.dueDateWarnings}>
      <Stack spacing={3}>
        <StepHeader
          title="Reviewer Preparation"
          description="Configure reviewers and the signer, then send the document for review."
        />

        <DetailsCard dueDateShift={s.detailsDueDateShift} />

        <ReviewerPreparationStatusBanners state={s} />

        <ReviewerPreparationStepBody state={s} submissionId={submissionId} />
      </Stack>

      <ReviewerPreparationStepDialogsWrapper
        state={s}
        submissionId={submissionId}
        facilityId={facilityId}
      />
    </SubmissionFormProvider>
  );
}
